import { ChevronDown, FileText, Home, LogOut, Ticket, User } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Navbar.css';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigateToDashboard = () => {
    navigate('/dashboard');
    setDropdownOpen(false);
  };

  const handleNavigateToProfile = () => {
    navigate('/profile');
    setDropdownOpen(false);
  };

  // Get credits display
  const getCreditsDisplay = () => {
    if (!user?.credits) return null;

    const total = user.credits.total;

    return (
      <div className="navbar-credits">
        <Ticket size={16} className="credits-icon" />
        <span className="credits-text">
          {total} {total === 1 ? 'credit' : 'credits'}
        </span>
      </div>
    );
  };

  // Don't show navbar on landing page or auth callback
  if (location.pathname === '/' || location.pathname === '/auth/callback') {
    return null;
  }

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-left">
          <button onClick={handleNavigateToDashboard} className="navbar-logo">
            <FileText size={20} className="logo-icon" />
            <span className="logo-text">ResumeIt</span>
          </button>
        </div>

        <div className="navbar-right">
          {getCreditsDisplay()}

          <div className="navbar-user" ref={dropdownRef}>
            <button
              className="user-button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || 'User'}
                  className="user-avatar"
                />
              ) : (
                <div className="user-avatar-placeholder">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <span className="user-name">{user?.name || 'User'}</span>
              <ChevronDown size={16} className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-user-info">
                    <div className="dropdown-user-name">{user?.name}</div>
                    <div className="dropdown-user-email">{user?.email}</div>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <button
                  className="dropdown-item"
                  onClick={handleNavigateToDashboard}
                >
                  <Home size={16} className="dropdown-icon" />
                  Dashboard
                </button>

                <button
                  className="dropdown-item"
                  onClick={handleNavigateToProfile}
                >
                  <User size={16} className="dropdown-icon" />
                  Profile
                </button>

                <div className="dropdown-divider" />

                <button
                  className="dropdown-item logout"
                  onClick={handleLogout}
                >
                  <LogOut size={16} className="dropdown-icon" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
